import React, { useState, useEffect } from 'react';
import { Drawer, Form, Input, Select, Switch, Button, Space, message } from 'antd';
import { updateSsoIntegration, SsoIntegration, SsoProvider } from '../../lib/integrationsApi';

const { TextArea } = Input;

interface EditIntegrationDrawerProps {
  visible: boolean;
  integration: SsoIntegration;
  onClose: () => void;
  onSuccess: () => void;
}

const EditIntegrationDrawer: React.FC<EditIntegrationDrawerProps> = ({
  visible,
  integration,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [provider] = useState<SsoProvider>(integration.provider);

  useEffect(() => {
    if (visible && integration) {
      const config = integration.config as any;

      form.setFieldsValue({
        name: integration.name,
        description: integration.description,
        provider: integration.provider,
        isActive: integration.isActive,
        ...(provider === 'teams' && {
          teamsClientId: config.clientId,
          teamsTenantId: config.tenantId,
          teamsRedirectUri: config.redirectUri,
          teamsScopes: config.scopes?.join(', '),
          captureTeamContext: config.captureTeamContext,
          captureChannelContext: config.captureChannelContext,
        }),
        ...(provider === 'maximo' && {
          maximoAuthUrl: config.authUrl,
          maximoApiUrl: config.apiUrl,
          maximoLoginType: config.loginType,
          maximoClientId: config.clientId,
        }),
        ...(provider === 'slack' && {
          slackClientId: config.clientId,
          slackRedirectUri: config.redirectUri,
          slackScopes: config.scopes?.join(', '),
          captureWorkspaceContext: config.captureWorkspaceContext,
        }),
      });
    }
  }, [visible, integration, form, provider]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const config: any = {};

      if (provider === 'teams') {
        config.clientId = values.teamsClientId;
        if (values.teamsClientSecret) config.clientSecret = values.teamsClientSecret;
        config.tenantId = values.teamsTenantId || 'common';
        config.redirectUri = values.teamsRedirectUri;
        config.scopes = values.teamsScopes?.split(',').map((s: string) => s.trim()) || ['User.Read'];
        config.captureTeamContext = values.captureTeamContext !== false;
        config.captureChannelContext = values.captureChannelContext !== false;
      } else if (provider === 'maximo') {
        config.authUrl = values.maximoAuthUrl;
        config.apiUrl = values.maximoApiUrl;
        config.loginType = values.maximoLoginType || 'oauth';
        if (values.maximoClientId) config.clientId = values.maximoClientId;
        if (values.maximoClientSecret) config.clientSecret = values.maximoClientSecret;
      } else if (provider === 'slack') {
        config.clientId = values.slackClientId;
        if (values.slackClientSecret) config.clientSecret = values.slackClientSecret;
        config.redirectUri = values.slackRedirectUri;
        config.scopes = values.slackScopes?.split(',').map((s: string) => s.trim()) || ['users:read', 'chat:write'];
        config.captureWorkspaceContext = values.captureWorkspaceContext !== false;
      }

      await updateSsoIntegration(integration.id, {
        name: values.name,
        description: values.description,
        isActive: values.isActive,
        config,
      });

      message.success('Integration updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update integration');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      title="Edit SSO Integration"
      placement="right"
      onClose={handleCancel}
      open={visible}
      width={600}
      extra={
        <Space>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            Update Integration
          </Button>
        </Space>
      }
      styles={{
        body: { paddingBottom: 80 }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', paddingRight: '12px' }}
      >
        <Form.Item label="Integration Name" name="name" rules={[{ required: true }]}>
          <Input placeholder="e.g., Company Microsoft Teams SSO" />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <TextArea rows={2} placeholder="Optional description" />
        </Form.Item>

        <Form.Item label="Provider" name="provider">
          <Select disabled>
            <Select.Option value="teams">Microsoft Teams</Select.Option>
            <Select.Option value="maximo">IBM Maximo</Select.Option>
            <Select.Option value="slack">Slack</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item label="Active" name="isActive" valuePropName="checked">
          <Switch />
        </Form.Item>

        {provider === 'teams' && (
          <>
            <h3 className="text-base font-semibold mb-3">Microsoft Teams Configuration</h3>
            <Form.Item label="Azure AD Client ID" name="teamsClientId" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Client Secret (leave empty to keep current)" name="teamsClientSecret">
              <Input.Password placeholder="Enter new secret or leave empty" />
            </Form.Item>
            <Form.Item label="Tenant ID" name="teamsTenantId">
              <Input />
            </Form.Item>
            <Form.Item label="Redirect URI" name="teamsRedirectUri" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Scopes (comma-separated)" name="teamsScopes">
              <Input />
            </Form.Item>
            <Form.Item label="Capture Team Context" name="captureTeamContext" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="Capture Channel Context" name="captureChannelContext" valuePropName="checked">
              <Switch />
            </Form.Item>
          </>
        )}

        {provider === 'maximo' && (
          <>
            <h3 className="text-base font-semibold mb-3">Maximo Configuration</h3>
            <Form.Item label="Auth URL" name="maximoAuthUrl" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item label="API URL" name="maximoApiUrl" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Login Type" name="maximoLoginType">
              <Select>
                <Select.Option value="oauth">OAuth</Select.Option>
                <Select.Option value="form">Form-based</Select.Option>
                <Select.Option value="basic">Basic Auth</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item label="Client ID" name="maximoClientId">
              <Input />
            </Form.Item>
            <Form.Item label="Client Secret (leave empty to keep current)" name="maximoClientSecret">
              <Input.Password placeholder="Enter new secret or leave empty" />
            </Form.Item>
          </>
        )}

        {provider === 'slack' && (
          <>
            <h3 className="text-base font-semibold mb-3">Slack Configuration</h3>
            <Form.Item label="Client ID" name="slackClientId" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Client Secret (leave empty to keep current)" name="slackClientSecret">
              <Input.Password placeholder="Enter new secret or leave empty" />
            </Form.Item>
            <Form.Item label="Redirect URI" name="slackRedirectUri" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Scopes (comma-separated)" name="slackScopes">
              <Input />
            </Form.Item>
            <Form.Item label="Capture Workspace Context" name="captureWorkspaceContext" valuePropName="checked">
              <Switch />
            </Form.Item>
          </>
        )}
      </Form>
    </Drawer>
  );
};

export default EditIntegrationDrawer;
